import React from 'react'
import { ReflectionTrack } from './ReflectionTrack.jsx'
import { SubgoalPickerCard } from './SubgoalPickerCard.jsx'
import { FreeTextCard } from './FreeTextCard.jsx'

const TAGS = ['FACT', 'CAUSE', 'CAUSE', 'LEARNING']

const CARDS = [
  { key: 'card1', title: 'What did you achieve?', placeholder: 'Pick the sub-goals you delivered.', notePlaceholder: 'Anything else you\'re proud of?' },
  { key: 'card2', title: 'What helped you get there?', placeholder: 'People, resources, habits, circumstances \u2014 what supported the win?' },
  { key: 'card3', title: 'What challenges did you overcome?', placeholder: 'What got in the way, and how did you get past it?' },
  { key: 'card4', title: 'What will you carry forward?', placeholder: 'What should you keep doing to stay successful? What did you change that led to greatness?' },
]

export function SuccessesTrack({
  data = {},
  onChange,
  managerNotes = {},
  onManagerNoteChange,
  createSuggestHandler,
  subgoals = [],
  snapshots = null,
  isReport,
  isManager,
  isShared,
  isReady,
  isClosed,
  reportName,
}) {
  const readOnly = isClosed || !isReport

  const eligibleSubgoals = subgoals.filter((sg) => sg.status === 'done')
  const selectedIds = data.card1?.subgoalIds || []
  const selectedSet = new Set(selectedIds)
  const allPickerSubgoals = [
    ...eligibleSubgoals,
    ...subgoals
      .filter((sg) => selectedSet.has(sg.subgoalId) && sg.status !== 'done')
      .map((sg) => ({ ...sg, _statusChanged: true })),
  ]
  const seen = new Set()
  const dedupedSubgoals = allPickerSubgoals.filter((sg) => {
    if (seen.has(sg.subgoalId)) return false
    seen.add(sg.subgoalId)
    return true
  })

  function countFilled() {
    let count = 0
    if (selectedIds.length > 0 || data.card1?.note) count++
    if (data.card2?.text) count++
    if (data.card3?.text) count++
    if (data.card4?.text) count++
    return count
  }

  function handleCardChange(cardKey, field, value) {
    const current = data[cardKey] || {}
    onChange?.({ ...data, [cardKey]: { ...current, [field]: value } })
  }

  function handleToggleSubgoal(subgoalId) {
    const current = data.card1?.subgoalIds || []
    const next = current.includes(subgoalId)
      ? current.filter((id) => id !== subgoalId)
      : [...current, subgoalId]
    handleCardChange('card1', 'subgoalIds', next)
  }

  return (
    <ReflectionTrack
      title="Successes"
      filledCount={countFilled()}
      sectionNote={managerNotes.section || ''}
      onSectionNoteChange={(v) => onManagerNoteChange?.({ ...managerNotes, section: v })}
      onSuggestSectionNote={createSuggestHandler?.('successes', 'section')}
      isManager={isManager}
      isShared={isShared}
      isReady={isReady}
      isClosed={isClosed}
      reportName={reportName}
    >
      <SubgoalPickerCard
        tag={TAGS[0]}
        title={CARDS[0].title}
        subgoals={dedupedSubgoals}
        selectedIds={selectedIds}
        onToggle={handleToggleSubgoal}
        note={data.card1?.note || ''}
        onNoteChange={(v) => handleCardChange('card1', 'note', v)}
        placeholder={CARDS[0].placeholder}
        notePlaceholder={CARDS[0].notePlaceholder}
        snapshots={isClosed ? snapshots : null}
        readOnly={readOnly}
        isClosed={isClosed}
        managerNote={managerNotes.card1 || ''}
        onManagerNoteChange={(v) => onManagerNoteChange?.({ ...managerNotes, card1: v })}
        onSuggestManagerNote={createSuggestHandler?.('successes', 'card1')}
        isManager={isManager}
        isReady={isReady}
        isShared={isShared}
      />

      {CARDS.slice(1).map((card, i) => (
        <FreeTextCard
          key={card.key}
          tag={TAGS[i + 1]}
          title={card.title}
          value={data[card.key]?.text || ''}
          onChange={(v) => handleCardChange(card.key, 'text', v)}
          placeholder={card.placeholder}
          readOnly={readOnly}
          isClosed={isClosed}
          managerNote={managerNotes[card.key] || ''}
          onManagerNoteChange={(v) => onManagerNoteChange?.({ ...managerNotes, [card.key]: v })}
          onSuggestManagerNote={createSuggestHandler?.('successes', card.key)}
          isManager={isManager}
          isReady={isReady}
          isShared={isShared}
        />
      ))}
    </ReflectionTrack>
  )
}
